import React, { FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../context/AuthContext';
import {
  authHintClass,
  authInputClass,
  authLabelClass,
  authLinkClass,
  authPrimaryButtonClass,
  type AuthFormTone,
} from './authFormStyles';

/** Must stay in sync with backend `mfaResendCooldownMs` in iam.service.ts */
const MFA_RESEND_COOLDOWN_SECONDS = 60;

type MfaFormProps = {
  onError: (message: string) => void;
  tone?: AuthFormTone;
};

function MfaForm({ onError, tone = 'light' }: MfaFormProps) {
  const { completeMfa, resendMfaCode, cancelMfaFlow, auth } = useAuth();
  const { t } = useTranslation();
  const [code, setCode] = React.useState('');
  const [submitting, setSubmitting] = React.useState(false);
  const [resending, setResending] = React.useState(false);
  const [resendCooldownSec, setResendCooldownSec] = React.useState(0);

  React.useEffect(() => {
    if (resendCooldownSec <= 0) return undefined;
    const id = window.setInterval(() => {
      setResendCooldownSec((s) => (s <= 1 ? 0 : s - 1));
    }, 1000);
    return () => window.clearInterval(id);
  }, [resendCooldownSec]);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    onError('');

    try {
      await completeMfa(code);
    } catch (err) {
      const message =
        err instanceof Error
          ? err.message
          : t('auth.mfa_invalid_code_default', { defaultValue: 'Invalid code.' });
      onError(message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleResend = async () => {
    if (resending || resendCooldownSec > 0) return;
    setResending(true);
    onError('');
    try {
      await resendMfaCode();
      setResendCooldownSec(MFA_RESEND_COOLDOWN_SECONDS);
    } catch (err) {
      const retry =
        err instanceof Error
          ? (err as Error & { retryAfterSeconds?: number }).retryAfterSeconds
          : undefined;
      if (typeof retry === 'number' && retry > 0) {
        setResendCooldownSec(retry);
      }
      const message =
        err instanceof Error
          ? err.message
          : t('auth.mfa_resend_failed', { defaultValue: 'Unable to resend code.' });
      onError(message);
    } finally {
      setResending(false);
    }
  };

  return (
    <form className="space-y-5" onSubmit={handleSubmit}>
      <div className="space-y-1.5">
        <label htmlFor="mfa-code" className={authLabelClass(tone)}>
          {t('auth.mfa_title')}
        </label>
        <input
          id="mfa-code"
          type="text"
          inputMode="numeric"
          maxLength={6}
          className={authInputClass(tone)}
          placeholder={t('auth.mfa_code_placeholder', { defaultValue: '123456' })}
          value={code}
          onChange={(e) => setCode(e.target.value)}
        />
      </div>

      <p className={authHintClass(tone)}>
        {t('auth.mfa_body_intro')}{' '}
        <span className="font-medium text-slate-800">{auth.email}</span>.{' '}
        {t('auth.mfa_body_outro')}
      </p>

      <button type="submit" disabled={submitting} className={authPrimaryButtonClass(tone)}>
        {submitting
          ? t('auth.mfa_verifying_state', { defaultValue: 'Verifying…' })
          : t('auth.mfa_button')}
      </button>

      <div className="flex flex-col gap-2 border-t border-slate-200 pt-4 sm:flex-row sm:items-center sm:justify-between">
        <button
          type="button"
          onClick={handleResend}
          disabled={resending || resendCooldownSec > 0}
          className={`${authLinkClass(tone)} text-left disabled:cursor-not-allowed disabled:text-slate-400 disabled:no-underline`}
        >
          {resendCooldownSec > 0
            ? t('auth.mfa_resend_wait', {
                seconds: resendCooldownSec,
                defaultValue: `Resend code in ${resendCooldownSec}s`,
              })
            : resending
              ? t('auth.mfa_resend_sending', { defaultValue: 'Sending…' })
              : t('auth.mfa_resend', { defaultValue: 'Resend code' })}
        </button>
        <button
          type="button"
          onClick={() => {
            onError('');
            cancelMfaFlow();
          }}
          className={`${authLinkClass(tone)} text-left sm:text-right`}
        >
          {t('auth.mfa_back_to_sign_in', {
            defaultValue: 'Back to sign in',
          })}
        </button>
      </div>

      <p className="text-[11px] leading-relaxed text-slate-500">
        {t('auth.mfa_resend_policy', {
          defaultValue:
            'You can resend up to 3 times per hour. If email still does not arrive, check spam or your server email (SES) settings.',
        })}
      </p>
    </form>
  );
}

export default MfaForm;
